import React, { useMemo, useState } from "react";
import {
    ProfileTabsComponent,
    ComponentConfig,
    ComponentType,
} from "../../../services/types/ComponentTypes";
import { useDarkMode } from "../../../contexts/DarkModeContext";
import { isComponentVisible } from "../../../utils/visibilityUtils";
import {
    canEditProfileSection,
    filterSectionsByCountry,
    type ProfileSectionConfig,
} from "../../../utils/hrmsProfileEdit";

interface ProfileTabsProps {
    component: ProfileTabsComponent;
    formData: any;
    handleFormDataChange: (fieldName: string, value: any) => void;
    handleActionTrigger: (actionId: string, data?: any) => void;
}

const EMPTY_PLACEHOLDER = "—";

/** Format a date string for display (e.g. API returns YYYY-MM-DD). */
function formatDateDisplay(value: string | number | undefined | null): string {
    if (value === undefined || value === null || value === "") return "";
    const str = String(value).trim();
    if (!str) return "";
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return str;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
}

/** Resolve display value for a profile view field from formData. */
function getProfileDisplayValue(
    field: ComponentConfig & { componentType?: string; label?: string; Json_Attribute?: string; BindValue?: string; lookupName?: string; LookupName?: string },
    formData: Record<string, any>
): string {
    const name = (field as any).name || (field as any).BindValue;
    const jsonAttr = (field as any).Json_Attribute;
    const keys = [name, jsonAttr].filter(Boolean) as string[];
    let raw: unknown = undefined;
    for (const k of keys) {
        if (formData[k] !== undefined && formData[k] !== null && formData[k] !== "") {
            raw = formData[k];
            break;
        }
    }
    if (raw === undefined || raw === null || raw === "") {
        const selectedKey = name ? `${name}_selectedData` : null;
        const selected = selectedKey ? formData[selectedKey] : null;
        if (selected && typeof selected === "object") {
            const label = selected.name ?? selected.label ?? selected.value ?? "";
            if (label !== undefined && label !== null && String(label).trim() !== "") return String(label);
        }
        return "";
    }
    const type = (field as any).componentType;
    if (type === ComponentType.DATE_FIELD || type === "DATE_FIELD" || (field as any).ControlType === "Date") {
        return formatDateDisplay(String(raw));
    }
    if (typeof raw === "boolean") return raw ? "Yes" : "No";
    return String(raw).trim();
}

const ProfileTabs: React.FC<ProfileTabsProps> = ({
    component,
    formData,
    handleFormDataChange,
    handleActionTrigger,
}) => {
    const { isDarkMode } = useDarkMode();

    const countryCode =
        formData?.country_code ||
        formData?.country ||
        formData?.primary_nationality ||
        null;

    const visibleTabs = useMemo(() => {
        return component.config.tabs.filter((tab) => {
            const tabConfig = component.config.tabConfigs[tab];
            if (!tabConfig) return true;
            return isComponentVisible(tabConfig.visibility);
        });
    }, [component.config.tabs, component.config.tabConfigs]);

    const [activeTab, setActiveTab] = useState(visibleTabs[0] || component.config.tabs[0]);

    const resolvedActiveTab = visibleTabs.includes(activeTab)
        ? activeTab
        : visibleTabs[0] || activeTab;

    const handleTabClick = (tab: string) => {
        setActiveTab(tab);
    };

    const currentTabConfig = component.config.tabConfigs[resolvedActiveTab];

    if (!currentTabConfig) {
        return null;
    }

    const visibleSections = useMemo(() => {
        const sections: ProfileSectionConfig[] = currentTabConfig.sections || [];
        return filterSectionsByCountry(sections, countryCode);
    }, [currentTabConfig.sections, countryCode]);

    const containerStyle = component.style as React.CSSProperties;
    const borderColor = isDarkMode ? "#334155" : "#e0e0e0";
    const tabInactiveColor = isDarkMode ? "#94a3b8" : "#666";
    const tabActiveColor = isDarkMode ? "#38bdf8" : "#007AFF";
    const sectionTitleColor = isDarkMode ? "#f1f5f9" : "#0f172a";
    const labelColor = isDarkMode ? "#94a3b8" : "#64748b";
    const valueColor = isDarkMode ? "#e2e8f0" : "#0f172a";
    const editBtnBorder = isDarkMode ? "#475569" : "#ddd";
    const editBtnColor = isDarkMode ? "#e2e8f0" : "#334155";
    const tabBg = isDarkMode ? "rgba(51,65,85,0.35)" : "rgba(226,232,240,0.55)";
    const tabActiveBg = isDarkMode ? "rgba(56,189,248,0.18)" : "rgba(56,189,248,0.14)";
    const cardBg = isDarkMode ? "rgba(15,23,42,0.35)" : "#ffffff";
    const cardBorder = isDarkMode ? "rgba(51,65,85,0.9)" : "rgba(148,163,184,0.35)";

    return (
        <div style={{ ...containerStyle, width: "100%" }}>
            <div
                style={{
                    display: "flex",
                    gap: 6,
                    padding: 6,
                    border: `1px solid ${borderColor}`,
                    borderRadius: 12,
                    background: tabBg,
                    marginBottom: 16,
                    overflowX: "auto",
                }}
            >
                {visibleTabs.map((tab) => (
                    <div
                        key={tab}
                        onClick={() => handleTabClick(tab)}
                        style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            fontWeight: 600,
                            color: resolvedActiveTab === tab ? tabActiveColor : tabInactiveColor,
                            textTransform: "capitalize",
                            borderRadius: 10,
                            whiteSpace: "nowrap",
                            background: resolvedActiveTab === tab ? tabActiveBg : "transparent",
                            border: resolvedActiveTab === tab ? `1px solid ${isDarkMode ? "rgba(56,189,248,0.45)" : "rgba(56,189,248,0.40)"}` : "1px solid transparent",
                            transition: "background-color 120ms ease, border-color 120ms ease, color 120ms ease",
                        }}
                    >
                        {tab}
                    </div>
                ))}
            </div>

            <div>
                {visibleSections.map((section) => (
                    <div
                        key={section.sectionId}
                        style={{
                            marginBottom: 14,
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: 14,
                            padding: 14,
                            boxShadow: isDarkMode ? "0 10px 24px rgba(0,0,0,0.25)" : "0 1px 2px rgba(2,6,23,0.06)",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: sectionTitleColor, letterSpacing: "-0.01em" }}>
                                {section.title}
                            </h3>
                            {section.editDrawerId &&
                                section.saveActionId &&
                                canEditProfileSection(section) &&
                                handleActionTrigger && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        handleActionTrigger(section.editDrawerId!, {
                                            editTitle: section.editTitle,
                                            editConfigId: section.editConfigId,
                                            saveActionId: section.saveActionId,
                                        });
                                    }}
                                    style={{
                                        background: isDarkMode ? "rgba(15,23,42,0.35)" : "rgba(248,250,252,0.9)",
                                        border: `1px solid ${editBtnBorder}`,
                                        padding: "6px 10px",
                                        borderRadius: 10,
                                        cursor: "pointer",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: editBtnColor,
                                    }}
                                >
                                    Edit
                                </button>
                            )}
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
                            {section.fields?.map((field: ComponentConfig, index: number) => {
                                const label = (field as any).label || (field as any).Label || (field as any).name || (field as any).BindValue || "—";
                                const displayValue = getProfileDisplayValue(field as any, formData);
                                return (
                                    <div key={index} style={{ marginBottom: 2 }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: labelColor, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
                                            {label}
                                        </div>
                                        <div style={{ fontSize: 13, color: valueColor }}>
                                            {displayValue || EMPTY_PLACEHOLDER}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProfileTabs;
